import logger from '../../log';
import xhrPromise from './xhrPromise';
import isDashManifestLive from './isDashManifestLive';
import getHLSPlaylistUrl from './getHLSPlaylistUrl';
import isHLSPlaylistLive from './isHLSPlaylistLive';
import { SourceType, PlayType } from '../constants';
import { getSourceTypeForMime } from './sourceTypeUtils';
import isSmoothManifestLive from './isSmoothManifestLive';

const log = logger.getLogger(__filename);

const getSource = (
  url: string,
  timeout: number
): Promise<{ request: XMLHttpRequest; data: string }> =>
  xhrPromise({ url, expectJson: false, timeout })
    .then(response => response)
    .catch(response => {
      if (response && response.status === 403) {
        return xhrPromise({
          url,
          expectJson: false,
          timeout,
          withCredentials: true
        }).then(response => response);
      } else {
        return Promise.reject(response);
      }
    });

const isHLSManifestLive = (
  manifest: string,
  manifestUrl: string
): Promise<boolean> => {
  const playlistUrl = getHLSPlaylistUrl(manifest, manifestUrl);
  if (!playlistUrl) {
    return Promise.reject();
  }
  return getSource(playlistUrl, 4000).then(response =>
    isHLSPlaylistLive(response.data)
  );
};

// Returns a promise that resolves to true / false or rejects
/**
 * Get the play type of the url with sourceType
 * @param obj - url and sourcetype
 * @returns a promise containing LIVE|VOD
 */
export default function requestPlayType({
  url,
  sourceType
}: {
  url: string;
  sourceType: SourceType;
}): Promise<PlayType> {
  return getSource(url, 4000)
    .then(({ data, request }) => {
      const manifest = data;
      const mime = request && request.getResponseHeader('content-type');
      const otherType = getSourceTypeForMime(mime, url);
      if (otherType && otherType !== sourceType) {
        log.warn(
          `content-type ${mime} does not match the inbound sourceType ${sourceType}`
        );
        sourceType = otherType;
      }
      switch (sourceType) {
        case SourceType.HLS:
          return isHLSManifestLive(manifest, url);
        case SourceType.DASH:
          return isDashManifestLive(manifest);
        case SourceType.SMOOTH:
          return isSmoothManifestLive(manifest);
        default:
          return Promise.reject();
      }
    })
    .then(isLive => (isLive ? PlayType.LIVE : PlayType.VOD))
    .catch(() => {
      throw Error('Play type cannot be determined');
    });
}
